import React, { useCallback, useEffect, useState } from "react";
import BookingPartners from "./BookingPartners";
import VenuePartnerLandingPage from "./VenuePartnerLandingPage";
import { supabase } from "../supabaseClient";
import { useAuthModal } from "../AuthModalContext";

export default function VenueJoinFlow() {
  const { openAuthModal } = useAuthModal();
  const [selected, setSelected] = useState(() => { try { const value = localStorage.getItem("venue_pricing_model"); return ["commission", "pro_partner"].includes(value) ? value : ""; } catch { return ""; } });
  const [authLoading, setAuthLoading] = useState(true);
  const [accessError, setAccessError] = useState("");
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [linkedWorkspaceId, setLinkedWorkspaceId] = useState(null);
  const [businessName, setBusinessName] = useState("");
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState("");

  const loadBusinessAccess = useCallback(async () => {
    setAuthLoading(true);
    setAccessError("");
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const nextUser = sessionData?.session?.user || null;
      setUser(nextUser);
      if (!nextUser?.id) {
        setProfile(null);
        setLinkedWorkspaceId(null);
        return;
      }
      const [{ data: profileData }, { data: membershipRows }] = await Promise.all([
        supabase.from("profiles").select("id,name,email,role,account_type,venue_model").eq("id", nextUser.id).maybeSingle(),
        supabase.from("workspace_members").select("workspace_id,workspace_kind,role").eq("profile_id", nextUser.id).limit(20),
      ]);
      setProfile(profileData || null);
      setBusinessName((profileData?.name || nextUser.user_metadata?.display_name || "").trim());
      const businessMembership = (membershipRows || []).find((row) => ["business", "venue"].includes(row.workspace_kind));
      setLinkedWorkspaceId(businessMembership?.workspace_id || null);
    } catch {
      setAccessError("We could not load your venue access. Check your connection and try again.");
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBusinessAccess();
    const { data } = supabase.auth.onAuthStateChange(() => {
      loadBusinessAccess();
    });
    return () => data?.subscription?.unsubscribe?.();
  }, [loadBusinessAccess]);

  const isBusinessLinked = !!linkedWorkspaceId || profile?.role === "partner" || profile?.account_type === "business" || profile?.account_type === "institute" || !!profile?.venue_model;

  const linkBusinessAccount = async (event) => {
    event.preventDefault();
    if (linking) return;
    const name = businessName.trim();
    if (!name) {
      setLinkError("Enter the business or venue account name.");
      return;
    }
    setLinking(true);
    setLinkError("");
    try {
      const { data, error } = await supabase.rpc("ensure_business_workspace", {
        p_name: name,
        p_kind: "venue",
        p_venue_model: selected || null,
      });
      if (error) throw error;
      setLinkedWorkspaceId(data || "linked");
      await loadBusinessAccess();
    } catch (err) {
      console.warn("[VenueJoinFlow] business link failed", err);
      setLinkError("We could not link your business account right now. Please check the name and try again.");
    } finally {
      setLinking(false);
    }
  };

  const signIn = () => openAuthModal({ mode: "signin", title: "Sign in to list a venue", body: "Use your Snows ProAm profile first. We will link venue management to that profile after sign in.", redirectTo: "/venues/join" });
  const choosePlan = (planId) => {
    try { localStorage.setItem("venue_pricing_model", planId); } catch {}
    setSelected(planId);
    if (!authLoading && !user) signIn();
  };
  const backToPlans = () => {
    try { localStorage.removeItem("venue_pricing_model"); } catch {}
    setSelected("");
    setLinkError("");
  };
  if (selected && !authLoading && !accessError && user && isBusinessLinked) return <BookingPartners />;
  return <VenuePartnerLandingPage
    selected={selected} onChoose={choosePlan} onBack={backToPlans}
    loading={authLoading} accessError={accessError} onRetry={loadBusinessAccess}
    user={user} onSignIn={signIn} businessName={businessName} onNameChange={setBusinessName}
    onLink={linkBusinessAccount} linking={linking} linkError={linkError}
  />;
}
